(function () {
  'use strict';

  function trackEvent(name, detail) {
    detail = detail || {};
    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: name }, detail));
    }
    if (window.console && console.debug) {
      console.debug('[ShoeDNA]', name, detail);
    }
  }

  var METHOD_LABELS = {
    outfit: 'My Favorite Outfit',
    instagram: 'Instagram Feed',
    three_looks: 'Three Favorite Looks',
    pinterest: 'Pinterest Board'
  };

  function isLikelyImage(file) {
    if (file.type && file.type.indexOf('image/') === 0) return true;
    // Some mobile browsers (notably iOS Safari with HEIC photos) omit the MIME type.
    // Fall back to checking the file extension so real photos aren't silently dropped.
    if (!file.type) return /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(file.name || '');
    return false;
  }

  var CLAUDE_SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  function convertToJpegIfNeeded(file) {
    var isHeic = /\.(heic|heif)$/i.test(file.name || '') || file.type === 'image/heic' || file.type === 'image/heif';
    var needsConversion = isHeic || !file.type || CLAUDE_SUPPORTED_TYPES.indexOf(file.type) === -1;

    if (!needsConversion) return Promise.resolve(file);

    return new Promise(function (resolve) {
      var settled = false;
      var done = function (result) {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      // Safety net: never let a slow/stuck decode leave the UI hanging forever.
      var timeoutId = setTimeout(function () { done(file); }, 8000);

      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        clearTimeout(timeoutId);
        var canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob(function (blob) {
          done(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file);
        }, 'image/jpeg', 0.9);
      };
      img.onerror = function () {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(url);
        done(file);
      };
      img.src = url;
    });
  }

  function ShoeDnaExperience(root) {
    this.root = root;
    this.state = { method: null, files: [], answers: {} };

    this.landingScreen = root.querySelector('[data-screen="landing"]');
    this.methodScreen = root.querySelector('[data-screen="method"]');
    this.startButton = root.querySelector('[data-start-button]');
    this.backButton = root.querySelector('[data-back-button]');
    this.methodGrid = root.querySelector('[data-method-grid]');
    this.methodCards = Array.prototype.slice.call(root.querySelectorAll('[data-method-card]'));
    this.uploadZone = root.querySelector('[data-upload-zone]');
    this.uploadSummaryText = root.querySelector('[data-upload-summary-text]');
    this.changeMethodButton = root.querySelector('[data-change-method]');
    this.dropzone = root.querySelector('[data-dropzone]');
    this.fileInput = root.querySelector('[data-file-input]');
    this.fileCountHint = root.querySelector('[data-file-count-hint]');
    this.debugStatus = root.querySelector('[data-debug-status]');
    this.previewsContainer = root.querySelector('[data-previews]');
    this.continueButton = root.querySelector('[data-continue-button]');

    this.backButtonQuestions = root.querySelector('[data-back-button-questions]');
    this.chipGroups = Array.prototype.slice.call(root.querySelectorAll('[data-chip-group]'));
    this.questionSelects = Array.prototype.slice.call(root.querySelectorAll('select[data-field]'));
    this.questionsContinueButton = root.querySelector('[data-questions-continue]');

    this.backButtonConsent = root.querySelector('[data-back-button-consent]');
    this.consentCheckbox = root.querySelector('[data-consent-checkbox]');
    this.consentContinueButton = root.querySelector('[data-consent-continue]');

    this.analysisMessages = Array.prototype.slice.call(root.querySelectorAll('[data-analysis-message]'));
    this.analysisError = root.querySelector('[data-analysis-error]');
    this.workerUrl = root.getAttribute('data-worker-url');

    this.revealPolaroid = root.querySelector('[data-reveal-polaroid]');
    this.revealPhoto = root.querySelector('[data-reveal-photo]');
    this.revealIdentity = root.querySelector('[data-reveal-identity]');
    this.revealQuote = root.querySelector('[data-reveal-quote]');
    this.revealLetter = root.querySelector('[data-reveal-letter]');
    this.revealTraits = root.querySelector('[data-reveal-traits]');
    this.revealSurprise = root.querySelector('[data-reveal-surprise]');
    this.revealContinueButton = root.querySelector('[data-reveal-continue]');

    this.matchHeroImage = root.querySelector('[data-match-hero-image]');
    this.matchHeroScore = root.querySelector('[data-match-hero-score]');
    this.matchHeroName = root.querySelector('[data-match-hero-name]');
    this.matchHeroButton = root.querySelector('[data-match-hero-button]');
    this.collectionList = root.querySelector('[data-collection-list]');
    this.matchesContinueButton = root.querySelector('[data-matches-continue]');

    this.shareCanvas = root.querySelector('[data-share-canvas]');
    this.shareButton = root.querySelector('[data-share-button]');
    this.shareReward = root.querySelector('[data-share-reward]');
    this.shareCode = root.querySelector('[data-share-code]');
    this.copyCodeButton = root.querySelector('[data-copy-code]');
    this.shareContinueButton = root.querySelector('[data-share-continue]');
    this.brandLogo = root.querySelector('[data-brand-logo]');
    this.shareUrl = root.getAttribute('data-share-url');

    this.emailForm = root.querySelector('[data-email-form]');
    this.emailInput = root.querySelector('[data-email-input]');
    this.emailSubmitButton = root.querySelector('[data-email-submit]');
    this.emailError = root.querySelector('[data-email-error]');

    this.finalIdentityTab = root.querySelector('[data-final-identity-tab]');
    this.finalStamp = root.querySelector('[data-final-stamp]');
    this.finalPhoto = root.querySelector('[data-final-photo]');
    this.finalIdentity = root.querySelector('[data-final-identity]');
    this.finalMatch = root.querySelector('[data-final-match]');

    this.bindLanding();
    this.bindMethodCards();
    this.bindUpload();
    this.bindQuestions();
    this.bindConsent();
    this.bindReveal();
    this.bindMatches();
    this.bindShare();
    this.bindEmail();
  }

  ShoeDnaExperience.prototype.goToScreen = function (screenName) {
    var screens = this.root.querySelectorAll('.dna-screen');
    var targetScreen = null;
    screens.forEach(function (screen) {
      var isTarget = screen.getAttribute('data-screen') === screenName;
      if (isTarget) {
        targetScreen = screen;
        screen.classList.remove('is-leaving');
        screen.classList.add('is-active');
        screen.setAttribute('aria-hidden', 'false');
      } else if (screen.classList.contains('is-active')) {
        screen.classList.remove('is-active');
        screen.classList.add('is-leaving');
        screen.setAttribute('aria-hidden', 'true');

        // Once the fade-out finishes, drop it out of layout flow entirely —
        // otherwise it keeps its full height (just invisible) and pushes
        // everything below it down the page.
        screen.addEventListener('animationend', function handler() {
          screen.removeEventListener('animationend', handler);
          screen.classList.remove('is-leaving');
        }, { once: true });
      }
    });

    if (targetScreen) {
      requestAnimationFrame(function () {
        targetScreen.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    var heading = this.root.querySelector('[data-screen="' + screenName + '"] h1, [data-screen="' + screenName + '"] h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  };

  ShoeDnaExperience.prototype.bindLanding = function () {
    var self = this;
    if (!this.startButton) return;
    this.startButton.addEventListener('click', function () {
      trackEvent('dna_started');
      self.goToScreen('method');
    });
  };

  ShoeDnaExperience.prototype.bindMethodCards = function () {
    var self = this;

    if (this.backButton) {
      this.backButton.addEventListener('click', function () {
        self.goToScreen('landing');
      });
    }

    this.methodCards.forEach(function (card) {
      card.addEventListener('click', function () {
        self.selectMethod(card);
      });
    });

    if (this.changeMethodButton) {
      this.changeMethodButton.addEventListener('click', function () {
        self.resetMethod();
      });
    }
  };

  ShoeDnaExperience.prototype.selectMethod = function (card) {
    var method = card.getAttribute('data-method');
    var maxFiles = parseInt(card.getAttribute('data-max-files'), 10) || 1;

    this.state.method = method;
    this.state.maxFiles = maxFiles;
    this.state.files = [];

    this.methodCards.forEach(function (c) {
      var selected = c === card;
      c.classList.toggle('is-selected', selected);
      c.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.methodGrid.hidden = true;
    this.uploadZone.hidden = false;
    this.uploadSummaryText.textContent = METHOD_LABELS[method] || method;
    if (maxFiles > 1) {
      this.fileInput.setAttribute('multiple', 'multiple');
    } else {
      this.fileInput.removeAttribute('multiple');
    }
    this.fileCountHint.textContent = maxFiles > 1
      ? 'Up to ' + maxFiles + ' photos'
      : '1 photo';

    this.renderPreviews();
    this.updateContinueState();

    var uploadZone = this.uploadZone;
    requestAnimationFrame(function () {
      uploadZone.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  ShoeDnaExperience.prototype.resetMethod = function () {
    this.state.method = null;
    this.state.files = [];
    this.methodCards.forEach(function (c) {
      c.classList.remove('is-selected');
      c.setAttribute('aria-pressed', 'false');
    });
    this.methodGrid.hidden = false;
    this.uploadZone.hidden = true;
    this.fileInput.value = '';
    this.renderPreviews();
  };

  ShoeDnaExperience.prototype.bindUpload = function () {
    var self = this;

    this.dropzone.addEventListener('click', function (e) {
      if (e.target !== self.fileInput) {
        self.fileInput.click();
      }
    });

    this.dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', function () {
      self.addFiles(self.fileInput.files);
      self.fileInput.value = '';
    });

    ['dragenter', 'dragover'].forEach(function (evtName) {
      self.dropzone.addEventListener(evtName, function (e) {
        e.preventDefault();
        self.dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (evtName) {
      self.dropzone.addEventListener(evtName, function (e) {
        e.preventDefault();
        self.dropzone.classList.remove('is-dragover');
      });
    });

    this.dropzone.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) {
        self.addFiles(e.dataTransfer.files);
      }
    });

    this.continueButton.addEventListener('click', function () {
      self.handleContinue();
    });
  };

  ShoeDnaExperience.prototype.setDebugStatus = function (text) {
    if (this.debugStatus) this.debugStatus.textContent = text || '';
  };

  ShoeDnaExperience.prototype.addFiles = function (fileList) {
    var self = this;

    if (!fileList || fileList.length === 0) {
      this.setDebugStatus('No se recibió ningún archivo del selector.');
      return;
    }

    var allNames = Array.prototype.map.call(fileList, function (f) {
      return f.name + ' (' + (f.type || 'sin tipo') + ', ' + Math.round(f.size / 1024) + 'KB)';
    }).join(', ');

    var incoming = Array.prototype.filter.call(fileList, isLikelyImage);

    if (incoming.length === 0) {
      this.setDebugStatus('Recibido pero no reconocido como imagen: ' + allNames);
      return;
    }

    var remaining = this.state.maxFiles - this.state.files.length;
    if (remaining <= 0) {
      this.setDebugStatus('Ya tienes el máximo de fotos para este método.');
      return;
    }

    incoming = incoming.slice(0, remaining);
    this.setDebugStatus('Procesando: ' + allNames + '…');

    Promise.all(incoming.map(convertToJpegIfNeeded)).then(function (converted) {
      self.state.files = self.state.files.concat(converted);
      self.renderPreviews();
      self.updateContinueState();
      self.setDebugStatus('Listo — ' + self.state.files.length + ' foto(s) agregada(s).');
    }).catch(function (err) {
      self.setDebugStatus('Error procesando la foto: ' + (err && err.message ? err.message : err));
    });
  };

  ShoeDnaExperience.prototype.removeFile = function (index) {
    this.state.files.splice(index, 1);
    this.renderPreviews();
    this.updateContinueState();
  };

  ShoeDnaExperience.prototype.renderPreviews = function () {
    var self = this;
    this.previewsContainer.innerHTML = '';

    this.state.files.forEach(function (file, index) {
      var wrap = document.createElement('div');
      wrap.className = 'dna-preview';

      var img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.alt = '';
      img.onload = function () {
        URL.revokeObjectURL(img.src);
      };

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'dna-preview__remove';
      removeBtn.setAttribute('aria-label', 'Remove photo');
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', function () {
        self.removeFile(index);
      });

      wrap.appendChild(img);
      wrap.appendChild(removeBtn);
      self.previewsContainer.appendChild(wrap);
    });

    var atMax = this.state.files.length >= this.state.maxFiles;
    this.dropzone.style.display = atMax ? 'none' : '';
  };

  ShoeDnaExperience.prototype.updateContinueState = function () {
    var hasFiles = this.state.files.length > 0;
    this.continueButton.disabled = !hasFiles;
    if (hasFiles) {
      trackEvent('upload_completed', { method: this.state.method, file_count: this.state.files.length });
    }
  };

  ShoeDnaExperience.prototype.handleContinue = function () {
    this.root.dispatchEvent(new CustomEvent('shoedna:method-complete', {
      bubbles: true,
      detail: { method: this.state.method, files: this.state.files }
    }));
    this.goToScreen('questions');
  };

  ShoeDnaExperience.prototype.bindQuestions = function () {
    var self = this;

    if (this.backButtonQuestions) {
      this.backButtonQuestions.addEventListener('click', function () {
        self.goToScreen('method');
      });
    }

    this.questionSelects.forEach(function (select) {
      var field = select.getAttribute('data-field');
      select.addEventListener('change', function () {
        self.state.answers[field] = select.value || null;
      });
    });

    this.chipGroups.forEach(function (group) {
      var field = group.getAttribute('data-chip-group');
      var chips = Array.prototype.slice.call(group.querySelectorAll('[data-chip-value]'));
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var alreadySelected = chip.classList.contains('is-selected');
          chips.forEach(function (c) { c.classList.remove('is-selected'); });
          if (alreadySelected) {
            self.state.answers[field] = null;
          } else {
            chip.classList.add('is-selected');
            self.state.answers[field] = chip.getAttribute('data-chip-value');
          }
        });
      });
    });

    if (this.questionsContinueButton) {
      this.questionsContinueButton.addEventListener('click', function () {
        self.handleQuestionsContinue();
      });
    }
  };

  ShoeDnaExperience.prototype.handleQuestionsContinue = function () {
    trackEvent('personality_questions_completed', this.state.answers);
    this.root.dispatchEvent(new CustomEvent('shoedna:questions-complete', {
      bubbles: true,
      detail: { answers: this.state.answers }
    }));
    this.goToScreen('consent');
  };

  ShoeDnaExperience.prototype.bindConsent = function () {
    var self = this;

    if (this.backButtonConsent) {
      this.backButtonConsent.addEventListener('click', function () {
        self.goToScreen('questions');
      });
    }

    if (this.consentCheckbox) {
      this.consentCheckbox.addEventListener('change', function () {
        self.consentContinueButton.disabled = !self.consentCheckbox.checked;
      });
    }

    if (this.consentContinueButton) {
      this.consentContinueButton.addEventListener('click', function () {
        self.handleConsentContinue();
      });
    }
  };

  ShoeDnaExperience.prototype.handleConsentContinue = function () {
    if (!this.consentCheckbox.checked) return;
    trackEvent('consent_accepted');
    this.root.dispatchEvent(new CustomEvent('shoedna:consent-complete', {
      bubbles: true
    }));
    this.goToScreen('analysis');
    this.runAnalysis();
  };

  var ANALYSIS_MESSAGE_DURATION = 1250;

  ShoeDnaExperience.prototype.runAnalysis = function () {
    var self = this;
    this.analysisError.hidden = true;

    var messagesDone = this.playAnalysisMessages();
    var fetchDone = this.fetchDnaResult();

    Promise.all([messagesDone, fetchDone]).then(function (results) {
      var result = results[1];
      self.state.result = result;
      trackEvent('ai_analysis_completed', { identity: result && result.identity });
      self.root.dispatchEvent(new CustomEvent('shoedna:analysis-complete', {
        bubbles: true,
        detail: { result: result }
      }));
      self.showReveal(result);
    }).catch(function (err) {
      console.error('[ShoeDNA] analysis failed', err);
      self.analysisError.hidden = false;
    });
  };

  ShoeDnaExperience.prototype.fetchProductInfo = function (productName) {
    if (!productName) return Promise.resolve(null);
    var url = '/search/suggest.json?q=' + encodeURIComponent(productName)
      + '&resources[type]=product&resources[limit]=1&resources[options][unavailable_products]=show';

    return fetch(url)
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products;
        var product = products && products.length && products[0];
        return product ? { image: product.image || null, url: product.url || null } : null;
      })
      .catch(function () { return null; });
  };

  ShoeDnaExperience.prototype.showReveal = function (result) {
    if (!result) return;
    var self = this;

    var topMatchName = result.top_match && result.top_match.name;
    this.fetchProductInfo(topMatchName).then(function (info) {
      self.revealPhoto.src = (info && info.image) || '';
    });

    this.revealIdentity.textContent = result.identity || '';
    this.revealQuote.textContent = result.identity_quote || '';
    this.revealLetter.textContent = result.letter || '';
    this.revealSurprise.textContent = result.surprise_observation || '';

    this.revealTraits.innerHTML = '';
    var traitsContainer = this.revealTraits;
    (result.signature_traits || []).forEach(function (trait) {
      var chip = document.createElement('span');
      chip.className = 'dna-reveal__trait';
      chip.textContent = trait;
      traitsContainer.appendChild(chip);
    });

    this.goToScreen('reveal');
    trackEvent('dna_revealed', { identity: result.identity });

    var polaroid = this.revealPolaroid;
    polaroid.classList.remove('is-revealed');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        polaroid.classList.add('is-revealed');
      });
    });
  };

  ShoeDnaExperience.prototype.bindReveal = function () {
    var self = this;
    if (!this.revealContinueButton) return;
    this.revealContinueButton.addEventListener('click', function () {
      self.root.dispatchEvent(new CustomEvent('shoedna:reveal-continue', {
        bubbles: true,
        detail: { result: self.state.result }
      }));
      self.showMatches(self.state.result);
    });
  };

  var COLLECTION_PHRASES = [
    function (name, score) { return name + ' shares your energy — a ' + score + '% match.'; },
    function (name, score) { return 'And ' + name + ' isn’t far behind, at ' + score + '%.'; }
  ];

  ShoeDnaExperience.prototype.showMatches = function (result) {
    if (!result || !result.top_match) return;
    var self = this;

    this.matchHeroScore.textContent = Math.round(result.top_match.score) + '%';
    this.matchHeroName.textContent = result.top_match.name;
    this.matchHeroButton.textContent = 'Meet ' + result.top_match.name;

    this.fetchProductInfo(result.top_match.name).then(function (info) {
      self.matchHeroImage.src = (info && info.image) || '';
      self.matchHeroButton.href = (info && info.url) || '#';
    });

    this.collectionList.innerHTML = '';
    (result.secondary_matches || []).forEach(function (match, index) {
      var link = document.createElement('a');
      link.className = 'dna-collection__link';
      link.href = '#';

      var img = document.createElement('img');
      img.className = 'dna-collection__image';
      img.alt = '';

      var text = document.createElement('p');
      text.className = 'dna-collection__text';
      var phraseFn = COLLECTION_PHRASES[index % COLLECTION_PHRASES.length];
      text.textContent = phraseFn(match.name, Math.round(match.score));

      link.appendChild(img);
      link.appendChild(text);

      var item = document.createElement('div');
      item.className = 'dna-collection__item';
      item.appendChild(link);
      self.collectionList.appendChild(item);

      link.addEventListener('click', function () {
        trackEvent('product_clicked', { name: match.name, position: 'secondary_match' });
      });

      self.fetchProductInfo(match.name).then(function (info) {
        img.src = (info && info.image) || '';
        link.href = (info && info.url) || '#';
      });
    });

    this.matchHeroButton.onclick = function () {
      trackEvent('product_clicked', { name: result.top_match.name, position: 'top_match' });
    };

    this.goToScreen('matches');
  };

  ShoeDnaExperience.prototype.bindMatches = function () {
    var self = this;
    if (!this.matchesContinueButton) return;
    this.matchesContinueButton.addEventListener('click', function () {
      self.goToScreen('share');
      self.buildShareCard(self.state.result);
    });
  };

  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve) {
      if (!src) { resolve(null); return; }
      var img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = src;
    });
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function drawRoundedImageCover(ctx, img, x, y, w, h, radius) {
    if (!img) return;
    ctx.save();
    roundRectPath(ctx, x, y, w, h, radius);
    ctx.clip();

    var imgRatio = img.width / img.height;
    var boxRatio = w / h;
    var drawW, drawH, drawX, drawY;
    if (imgRatio > boxRatio) {
      drawH = h;
      drawW = h * imgRatio;
      drawX = x - (drawW - w) / 2;
      drawY = y;
    } else {
      drawW = w;
      drawH = w / imgRatio;
      drawX = x;
      drawY = y - (drawH - h) / 2;
    }
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }

  function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    var words = (text || '').split(' ');
    var line = '';
    var lines = [];
    for (var i = 0; i < words.length; i++) {
      var testLine = line + words[i] + ' ';
      if (ctx.measureText(testLine).width > maxWidth && line !== '') {
        lines.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    lines.forEach(function (l, index) {
      ctx.fillText(l, x, y + index * lineHeight);
    });
    return lines.length * lineHeight;
  }

  ShoeDnaExperience.prototype.generateQrDataUrl = function (text, size) {
    return new Promise(function (resolve) {
      if (!window.QRCode || !text) { resolve(null); return; }
      var container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      new window.QRCode(container, {
        text: text,
        width: size,
        height: size,
        colorDark: '#2A2A2A',
        colorLight: '#FBF7F1'
      });
      setTimeout(function () {
        var canvasEl = container.querySelector('canvas');
        var imgEl = container.querySelector('img');
        var dataUrl = canvasEl ? canvasEl.toDataURL('image/png') : (imgEl ? imgEl.src : null);
        document.body.removeChild(container);
        resolve(dataUrl);
      }, 80);
    });
  };

  ShoeDnaExperience.prototype.buildShareCard = function (result) {
    if (!result || !this.shareCanvas || !result.top_match) return;
    var self = this;

    var canvas = this.shareCanvas;
    var ctx = canvas.getContext('2d');
    var W = canvas.width;
    var H = canvas.height;

    var customerPhotoSrc = this.state.files[0] ? URL.createObjectURL(this.state.files[0]) : null;
    var fontsReady = (document.fonts && document.fonts.ready) || Promise.resolve();

    Promise.all([
      fontsReady,
      loadImage(customerPhotoSrc, false),
      this.fetchProductInfo(result.top_match.name).then(function (info) {
        return loadImage(info && info.image, true);
      }),
      loadImage(this.brandLogo ? this.brandLogo.src : null, true),
      this.generateQrDataUrl(this.shareUrl, 300).then(function (dataUrl) {
        return loadImage(dataUrl, false);
      })
    ]).then(function (imgs) {
      var customerImg = imgs[1];
      var matchImg = imgs[2];
      var logoImg = imgs[3];
      var qrImg = imgs[4];

      var bgGradient = ctx.createLinearGradient(0, 0, 0, H);
      bgGradient.addColorStop(0, '#ECDDFE');
      bgGradient.addColorStop(0.45, '#F6EEE3');
      bgGradient.addColorStop(1, '#E1FB62');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, W, H);

      ctx.fillStyle = 'rgba(42,42,42,0.6)';
      ctx.font = '700 28px "Fraunces"';
      ctx.textAlign = 'center';
      ctx.fillText('YOUR SHOE DNA™', W / 2, 120);

      var photoSize = 640;
      var photoX = (W - photoSize) / 2;
      var photoY = 170;
      if (customerImg) {
        drawRoundedImageCover(ctx, customerImg, photoX, photoY, photoSize, photoSize, 32);
      } else {
        ctx.fillStyle = '#ECDDFE';
        roundRectPath(ctx, photoX, photoY, photoSize, photoSize, 32);
        ctx.fill();
      }

      ctx.fillStyle = '#2A2A2A';
      ctx.font = '900 76px "Fraunces"';
      ctx.textAlign = 'center';
      var identityY = photoY + photoSize + 110;
      var identityHeight = wrapText(ctx, result.identity || '', W / 2, identityY, W - 160, 84);

      ctx.fillStyle = 'rgba(42,42,42,0.7)';
      ctx.font = 'italic 400 34px "Playfair Display"';
      var quoteY = identityY + identityHeight + 30;
      var quoteHeight = wrapText(ctx, result.identity_quote || '', W / 2, quoteY, W - 220, 44);

      var matchY = quoteY + quoteHeight + 60;
      var matchImgSize = 160;
      var matchImgX = W / 2 - matchImgSize - 20;
      if (matchImg) {
        drawRoundedImageCover(ctx, matchImg, matchImgX, matchY, matchImgSize, matchImgSize, 20);
      }

      ctx.textAlign = 'left';
      ctx.fillStyle = 'rgba(42,42,42,0.55)';
      ctx.font = '700 24px "DM Sans"';
      ctx.fillText('YOUR MATCH', matchImgX + matchImgSize + 30, matchY + 50);
      ctx.fillStyle = '#2A2A2A';
      ctx.font = '700 42px "Playfair Display"';
      ctx.fillText(result.top_match.name || '', matchImgX + matchImgSize + 30, matchY + 100);
      ctx.font = '600 30px "DM Sans"';
      ctx.fillText(Math.round(result.top_match.score) + '% match', matchImgX + matchImgSize + 30, matchY + 145);

      var margin = 60;
      var footerY = H - 190;

      if (logoImg) {
        var logoH = 56;
        var logoW = logoImg.width * (logoH / logoImg.height);
        ctx.drawImage(logoImg, margin, footerY, logoW, logoH);
      }

      if (qrImg) {
        var qrSize = 150;
        var qrX = W - margin - qrSize;
        var qrY = footerY - 10;
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

        ctx.fillStyle = 'rgba(42,42,42,0.6)';
        ctx.font = '600 22px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Descubre el tuyo', qrX + qrSize / 2, qrY + qrSize + 34);
      }
    });
  };

  ShoeDnaExperience.prototype.bindShare = function () {
    var self = this;

    if (this.shareButton) {
      this.shareButton.addEventListener('click', function () {
        self.handleShareClick();
      });
    }

    if (this.copyCodeButton) {
      this.copyCodeButton.addEventListener('click', function () {
        var code = self.shareCode.textContent.trim();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).catch(function () {});
        }
        trackEvent('discount_claimed', { code: code });
        self.copyCodeButton.textContent = 'Copiado ✓';
        setTimeout(function () {
          self.copyCodeButton.textContent = 'Copiar';
        }, 2000);
      });
    }

    if (this.shareContinueButton) {
      this.shareContinueButton.addEventListener('click', function () {
        self.root.dispatchEvent(new CustomEvent('shoedna:share-continue', { bubbles: true }));
        self.goToScreen('email');
      });
    }
  };

  ShoeDnaExperience.prototype.handleShareClick = function () {
    var self = this;
    trackEvent('share_clicked');

    this.shareCanvas.toBlob(function (blob) {
      if (!blob) return;
      var file = new File([blob], 'your-shoe-dna.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({
          files: [file],
          title: 'Your Shoe DNA',
          text: 'I just discovered my Shoe DNA ✨'
        }).catch(function () {});
      } else {
        var link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'your-shoe-dna.png';
        link.click();
      }

      self.shareReward.hidden = false;
    }, 'image/png');
  };

  ShoeDnaExperience.prototype.bindEmail = function () {
    var self = this;
    if (!this.emailForm) return;

    this.emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      self.handleEmailSubmit();
    });
  };

  ShoeDnaExperience.prototype.handleEmailSubmit = function () {
    var self = this;
    var email = this.emailInput.value.trim();
    if (!email) return;

    this.emailError.hidden = true;
    this.emailSubmitButton.disabled = true;

    var formData = new FormData(this.emailForm);

    fetch(this.emailForm.action, { method: 'POST', body: formData })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Contact form responded with status ' + response.status);
        }
        trackEvent('email_captured', { email: email });
        self.root.dispatchEvent(new CustomEvent('shoedna:email-complete', {
          bubbles: true,
          detail: { email: email }
        }));
        self.emailSubmitButton.disabled = false;
        self.emailSubmitButton.textContent = 'Saved ✓';
        self.showFinal(self.state.result);
      })
      .catch(function (err) {
        console.error('[ShoeDNA] email capture failed', err);
        self.emailError.hidden = false;
        self.emailSubmitButton.disabled = false;
      });
  };

  ShoeDnaExperience.prototype.showFinal = function (result) {
    if (!result) return;

    this.finalIdentityTab.textContent = result.identity || 'Shoe DNA';
    this.finalIdentity.textContent = result.identity || '';
    this.finalMatch.textContent = result.top_match ? ('Matched with ' + result.top_match.name) : '';

    if (this.state.files[0]) {
      this.finalPhoto.src = URL.createObjectURL(this.state.files[0]);
    }

    var today = new Date();
    this.finalStamp.textContent = 'Archived — ' + today.toLocaleDateString('es-MX', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    this.goToScreen('final');
  };

  ShoeDnaExperience.prototype.playAnalysisMessages = function () {
    var messages = this.analysisMessages;
    messages.forEach(function (m) { m.classList.remove('is-active'); });

    return new Promise(function (resolve) {
      var index = 0;
      function showNext() {
        if (index > 0) messages[index - 1].classList.remove('is-active');
        if (index >= messages.length) {
          resolve();
          return;
        }
        messages[index].classList.add('is-active');
        index += 1;
        setTimeout(showNext, ANALYSIS_MESSAGE_DURATION);
      }
      showNext();
    });
  };

  ShoeDnaExperience.prototype.fetchDnaResult = function () {
    var formData = new FormData();
    this.state.files.forEach(function (file) {
      formData.append('image', file);
    });
    var answers = this.state.answers;
    if (answers.zodiac) formData.append('zodiac', answers.zodiac);
    if (answers.coffee) formData.append('coffee', answers.coffee);
    if (answers.season) formData.append('season', answers.season);
    if (answers.color) formData.append('color', answers.color);

    return fetch(this.workerUrl, { method: 'POST', body: formData }).then(function (response) {
      if (!response.ok) {
        throw new Error('Worker responded with status ' + response.status);
      }
      return response.json();
    });
  };

  // Works around third-party popup/notification apps (fixed-position, high z-index)
  // that sit on top of our buttons and silently swallow taps/clicks — both an
  // empty "phantom" modal overlay from one app, and a fake-sale notification
  // widget from another. Neutralized only on this page, only while our
  // experience is present, so the rest of the site is unaffected.
  function neutralizePhantomOverlays() {
    document.querySelectorAll('.react-responsive-modal-root').forEach(function (root) {
      var container = root.querySelector('.react-responsive-modal-container');
      var hasVisibleModal = container && container.children.length > 0;
      root.style.pointerEvents = hasVisibleModal ? '' : 'none';
    });

    var salePopup = document.getElementById('fake-sale-popup');
    if (salePopup) {
      salePopup.style.pointerEvents = 'none';
    }
  }

  function init() {
    document.querySelectorAll('[data-shoe-dna]').forEach(function (root) {
      new ShoeDnaExperience(root);
    });

    neutralizePhantomOverlays();
    setInterval(neutralizePhantomOverlays, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
